import { useRef, type ReactNode, type Ref } from "react";
import { useTopLayerMenu } from "./useTopLayerMenu";

/**
 * A LIBRARY ROW'S `⋯` — the button and its list, the list drawn in the TOP LAYER.
 *
 * 🔴 W85 (his report, 2026-09-27): *"if i click these 3 dots in the last 1,2 or even 3 workouts, the container or
 * whatever that is of these extra choices are not shown cause they are limited by the main container. they need to be in
 * front."* MEASURED 2026-09-27 on the stub (14 workouts, the last row's `⋯` opened): the list was an `absolute` child of
 * its cell, and the TABLE itself is the clip — `main.library-page .ours-rows` carries `overflow: hidden` (pro.css, so the
 * card's 16 px corners hold). The list opened at y 771.9 → 918.7 against the table's bottom edge at 788.5: 17 px of a
 * 147 px menu showed. The exercises table is the same card and the same `absolute` list, so both rows use this.
 *
 * ⚠️ NOT A Z-INDEX: no z-index escapes an ancestor's `overflow: hidden`. The list goes where the day and week menus went
 * for the same reason (W79) — `useTopLayerMenu`, ONE mechanism: a popover in the top layer, placed under its `⋯` by
 * `menuPlacement` and following it on scroll and resize. The list stays the row's DOM child, so the page's outside-click
 * test (`wrapRef.contains`) and its Escape still close it unchanged.
 */
export function ShelfMore({ open, label, onToggle, wrapRef, children }: {
  readonly open: boolean;
  /** The row's name — the button reads `More for <name>`, the list `Actions for <name>`. */
  readonly label: string;
  readonly onToggle: () => void;
  /** The page's outside-click ref, attached to the open row only. */
  readonly wrapRef?: Ref<HTMLDivElement>;
  readonly children: ReactNode;
}) {
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  useTopLayerMenu(open, trigger, list);
  return (
    <div className="shelf-more" ref={wrapRef}>
      <button ref={trigger} type="button" className="shelf-more-button" aria-expanded={open}
              aria-label={`More for ${label}`} onClick={onToggle}>
        <span aria-hidden="true">⋯</span>
      </button>
      {open && (
        <div ref={list} className="shelf-menu is-top-layer" role="group" aria-label={`Actions for ${label}`}>
          {children}
        </div>
      )}
    </div>
  );
}
